import { Spinner } from "@/components/common/loading/spinner"
import Block from "@/components/wrappers/block"
import { fetchAllWildNeighbors } from "@/lib/api/wildNeighbors"
import { AnimatePresence, motion } from "framer-motion"
import GoogleMapReact from "google-map-react"
import { useEffect, useRef, useState } from "react"
import useSupercluster from "use-supercluster"
import { ActivityList } from "./activity"
import { Filter } from "./filter"
import { useRouter } from "next/router"
import mapStyles from "./styles"

const mapKey = process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY

const defaultZoom = 6
const defaultCenter = {
  lat: 59.36966,
  lng: 16.510115
}

const dropIn = {
  hidden: {
    y: "20px",
    opacity: 0
  },
  visible: {
    y: "0",
    opacity: 1,
    transition: {
      duration: 0.1,
      type: "spring",
      damping: 25,
      stiffness: 500
    }
  },
  exit: {
    y: "20px",
    opacity: 0,
    transition: {
      duration: 0.1,
      type: "spring",
      damping: 25,
      stiffness: 500
    }
  }
}

export default function WildNeighborsMap({ block }) {
  const mapRef = useRef()
  const [bounds, setBounds] = useState(null)
  const [zoom, setZoom] = useState(10)
  const [loading, setLoading] = useState(true)
  const [markers, setMarkers] = useState([])
  const [markersOriginal, setMarkersOriginal] = useState([])
  const [currentMarker, setCurrentMarker] = useState([])
  const [search, setSearch] = useState("")
  const [activities, setActivities] = useState([])
  const [grade, setGrade] = useState("0")
  const { clusters, supercluster } = useSupercluster({
    points: markers.map((marker, index) => ({
      id: index,
      type: "Feature",
      properties: {
        cluster: false
      },
      geometry: {
        type: "Point",
        coordinates: [
          parseFloat(marker.school_position.lng || 0),
          parseFloat(marker.school_position.lat || 0)
        ]
      },
      marker: marker
    })),
    bounds,
    zoom,
    options: { radius: 75, maxZoom: 15 }
  })
  const router = useRouter()

  useEffect(() => {
    let newMarkers = [...markersOriginal]

    setMarkers(markersOriginal)
    setCurrentMarker([])

    if (mapRef.current) {
      mapRef.current.setZoom(defaultZoom)
      mapRef.current.panTo(defaultCenter)
    }

    if (grade !== "0") {
      newMarkers = newMarkers.filter((marker) => {
        return marker.grades.includes(grade)
      })
    }

    if (search.length > 0) {
      newMarkers = newMarkers.filter((marker) => {
        return (
          marker.school_name.toLowerCase().includes(search.toLowerCase()) ||
          marker.class_name.toLowerCase().includes(search.toLowerCase()) ||
          marker.keywords.toLowerCase().includes(search.toLowerCase())
        )
      })
    }

    if (activities.length > 0) {
      newMarkers = newMarkers.filter((marker) => {
        const hasActivities = marker.activities
          .map((activity) => activity.activity)
          .some((activity) => activities.includes(activity))

        return hasActivities
      })
    }

    setMarkers(newMarkers)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activities])

  const getMarkers = async (map) => {
    mapRef.current = map

    try {
      const response = await fetchAllWildNeighbors()

      setMarkers(response.data || [])
      setMarkersOriginal(response.data || [])
      setLoading(false)
    } catch (error) {
      setLoading(true)
    }
  }

  const handleMarkerClick = (marker) => {
    router.push("#map")
    const groupMarkers = markers.filter((m) => {
      return (
        m.school_position.lat === marker.school_position.lat &&
        m.school_position.lng === marker.school_position.lng
      )
    }) || [marker]

    setCurrentMarker(groupMarkers)
  }

  return (
    <Block id="map" block={block}>
      <Filter
        block={block}
        setSearch={setSearch}
        setGrade={setGrade}
        setActivities={setActivities}
      />
      <div className="relative w-full h-[60vh] md:h-[700px] bg-white flex">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black-100/10">
            <Spinner
              size="large"
              borderClass="border-black-5"
              spinColor="rgb(185 210 50)"
              className="mr-2"
            />
          </div>
        )}
        <GoogleMapReact
          className="relative"
          bootstrapURLKeys={{
            key: mapKey
          }}
          defaultCenter={defaultCenter}
          defaultZoom={defaultZoom}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={async ({ map }) => getMarkers(map)}
          options={{
            styles: mapStyles,
            fullscreenControl: false,
            zoomControl: true,
            scrollwheel: false,
            disableDefaultUI: true
          }}
          disableDoubleClickZoom={true}
          onChange={({ zoom, bounds }) => {
            setZoom(zoom)
            setBounds([
              bounds.nw.lng,
              bounds.se.lat,
              bounds.se.lng,
              bounds.nw.lat
            ])
          }}>
          {clusters.map((cluster) => {
            const [lng, lat] = cluster.geometry.coordinates
            const { cluster: isCluster, point_count: pointCount } =
              cluster.properties

            if (isCluster) {
              return (
                <Cluster
                  key={`cluster-${cluster.id}`}
                  cluster={cluster}
                  pointCount={pointCount}
                  lat={lat}
                  lng={lng}
                  mapRef={mapRef}
                  supercluster={supercluster}
                />
              )
            }

            return (
              <Marker
                key={`marker-${cluster.marker.school_name}-${cluster.marker.class_name}-${cluster.id}`}
                lat={lat}
                lng={lng}
                marker={cluster.marker}
                onClick={() => handleMarkerClick(cluster.marker)}></Marker>
            )
          })}
        </GoogleMapReact>
        <AnimatePresence exitBeforeEnter={true}>
          {currentMarker.length > 0 && (
            <motion.div
              className="absolute bottom-0 left-0 z-10 w-full bg-white shadow-lg sm:bottom-6 sm:left-6 sm:w-84"
              variants={dropIn}
              initial="hidden"
              animate="visible"
              exit="exit">
              <ActivityList
                activities={block.attrs?.activities || []}
                markers={currentMarker}
                setCurrentMarker={setCurrentMarker}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Block>
  )
}

function Marker({ lng, lat, onClick }) {
  return (
    <button
      aria-label="Se aktiviter"
      type="button"
      className="w-6 h-6 -translate-x-1/2 -translate-y-1/2 rounded-full cursor-pointer bg-summer-green-100"
      data-lat={lng}
      data-lng={lat}
      onClick={onClick}
    />
  )
}

function Cluster({ cluster, pointCount, lat, lng, mapRef, supercluster }) {
  return (
    <button
      aria-label="Se aktiviter"
      data-lat={lat}
      data-lng={lng}
      className="flex items-center justify-center w-10 h-10 -translate-x-1/2 -translate-y-1/2 rounded-full cursor-pointer bg-summer-green-100"
      onClick={() => {
        const expansionZoom = Math.min(
          supercluster.getClusterExpansionZoom(cluster.id),
          16
        )
        mapRef.current.setZoom(expansionZoom)
        mapRef.current.panTo({ lat, lng })
      }}>
      <span className="font-serif text-base font-bold text-black-100">
        {pointCount}
      </span>
    </button>
  )
}
